import type { OfflineRequest } from "./offline-outbox";
import { queueGroupRevision, recoveryBlockReason } from "./offline-recovery-model";

export type ReplacementPhoto = { fileName: string; mimeType: string; contentBase64: string; note?: string };
export type PhotoReplacementCommand = {
  groupId: string;
  revision: string;
  base: string;
  photo: ReplacementPhoto;
  evidenceDedupeKey: string;
  completionDedupeKey: string;
};
export type ArchivedOfflineRequest = OfflineRequest & {
  archive: {
    reason: "photo_replaced";
    archivedAt: string;
    replacementGroupId: string;
    replacementRequestIds: string[];
    replacementDedupeKeys: string[];
  };
};
export type PhotoReplacementPlan = { archived: ArchivedOfflineRequest[]; replacements: OfflineRequest[] };

function parsedBody(item: OfflineRequest, description: string) {
  try {
    const body = JSON.parse(item.body) as Record<string, unknown>;
    if (!body || Array.isArray(body) || typeof body !== "object") throw new Error();
    return body;
  } catch {
    throw new Error(`${description}请求体无法解析，保留原记录并联系管理员核对`);
  }
}
function taskIdFromPath(path: string, suffix: string) {
  const match = path.match(new RegExp(`^/api/mobile/worker/tasks/([^/]+)/${suffix}$`));
  if (!match) return "";
  try { return decodeURIComponent(match[1]); } catch { return ""; }
}
function safeCycle(value: unknown) { return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 ? value : 0; }
function activeRows(items: OfflineRequest[], scopeKey: string, groupId: string) {
  return items.filter(item => item.scopeKey === scopeKey && item.groupId === groupId);
}
function validatePhoto(photo: ReplacementPhoto) {
  const fileName = photo.fileName.trim(), mimeType = photo.mimeType.trim().toLowerCase(), contentBase64 = photo.contentBase64.trim();
  if (!fileName || /[\\/\x00-\x1f]/.test(fileName)) throw new Error("替换照片文件名无效");
  if (!/^image\/(png|jpe?g|gif|webp|bmp|avif)$/.test(mimeType)) throw new Error("替换文件必须是受支持的图片格式");
  if (!new RegExp(`^data:${mimeType.replace("+", "\\+")};base64,[A-Za-z0-9+/]+={0,2}$`, "i").test(contentBase64)) throw new Error("替换照片内容无效，请重新选择原始图片文件");
  return { fileName, mimeType, contentBase64, note: String(photo.note ?? "").slice(0, 4000) };
}

/** 返回不能安全替换的原因；空字符串表示可替换。 */
export function photoReplacementBlockReason(items: OfflineRequest[], base: string) {
  if (!items.length) return "该组已变化或已补传，请刷新队列";
  const recoveryReason = recoveryBlockReason(items, base);
  if (recoveryReason) return recoveryReason;
  const uploadRows = items.filter(item => item.sequence === 0 && item.path.endsWith("/evidence/upload"));
  const completionRows = items.filter(item => item.sequence === 1 && item.path.endsWith("/action"));
  if (items.length !== 2 || uploadRows.length !== 1 || completionRows.length !== 1) return "仅支持替换标准的“完工照片 → 完工确认”两步队列";
  const upload = uploadRows[0], completion = completionRows[0];
  if (upload.status !== "failed") return "只有明确失败的完工照片可以安全替换";
  if (upload.method.toUpperCase() !== "POST" || completion.method.toUpperCase() !== "POST") return "旧请求方法异常，不能自动替换";
  let uploadBody: Record<string, unknown>, completionBody: Record<string, unknown>;
  try { uploadBody = parsedBody(upload, "旧照片上传"); completionBody = parsedBody(completion, "旧完工确认"); }
  catch (error) { return error instanceof Error ? error.message : "旧请求无法解析"; }
  const uploadTask = taskIdFromPath(upload.path, "evidence/upload"), completionTask = taskIdFromPath(completion.path, "action");
  if (!uploadTask || uploadTask !== completionTask) return "照片与完工确认不属于同一任务，不能自动替换";
  const uploadCycle = safeCycle(uploadBody.expectedAssignmentCycle), completionCycle = safeCycle(completionBody.expectedAssignmentCycle);
  if (!uploadCycle || uploadCycle !== completionCycle) return "旧请求缺少一致且可信的派工轮次，不能自动替换";
  if (uploadBody.idempotencyKey !== upload.dedupeKey || completionBody.idempotencyKey !== completion.dedupeKey || completionBody.action !== "complete") return "旧请求幂等标识或完工动作不一致，不能自动替换";
  if (uploadBody.stage !== "after" || uploadBody.evidenceType !== "photo") return "旧上传请求不是标准完工照片，不能自动替换";
  const expectedOperationKey = `complete:${uploadTask}:${uploadCycle}`;
  if (!upload.operationKey || upload.operationKey !== completion.operationKey || upload.operationKey !== expectedOperationKey) return "旧照片与完工确认缺少一致且可信的业务标识，不能自动替换";
  return "";
}

/**
 * 生成原记录归档与新请求。调用方必须在同一持久化事务内完成归档、移除旧队列记录和写入新记录。
 * 新请求沿用原任务、原派工轮次及其他可信上下文，但照片内容、请求记录和两个幂等键全部重新生成。
 */
export function planFailedPhotoReplacement(
  scopeKey: string,
  items: OfflineRequest[],
  command: PhotoReplacementCommand,
  createId: () => string,
  now = new Date().toISOString()
): PhotoReplacementPlan {
  const group = activeRows(items, scopeKey, command.groupId);
  if (!group.length || queueGroupRevision(group) !== command.revision) throw new Error("队列已变化，请重新打开处理窗口后核对");
  const reason = photoReplacementBlockReason(group, command.base);
  if (reason) throw new Error(reason);
  const keys = [command.evidenceDedupeKey.trim(), command.completionDedupeKey.trim()];
  if (keys.some(key => !key || key.length > 255) || keys[0] === keys[1]) throw new Error("替换请求的幂等标识无效");
  const existingKeys = new Set(items.filter(item => item.scopeKey === scopeKey).map(item => item.dedupeKey));
  if (keys.some(key => existingKeys.has(key))) throw new Error("替换请求标识已存在，请重新选择照片后重试");
  const photo = validatePhoto(command.photo);
  const upload = group.find(item => item.sequence === 0)!, completion = group.find(item => item.sequence === 1)!;
  const uploadBody = parsedBody(upload, "旧照片上传"), completionBody = parsedBody(completion, "旧完工确认");
  const replacementIds = [createId(), createId()];
  if (!replacementIds[0] || !replacementIds[1] || replacementIds[0] === replacementIds[1] || items.some(item => replacementIds.includes(item.id))) throw new Error("无法生成唯一的替换请求记录");
  const replacementGroupId = `${upload.operationKey || "completion"}:replacement:${keys[1]}`;
  const recovery = { paused: false, changedAt: now, ...(upload.recovery?.serverConfirmedAt ? { serverConfirmedAt: upload.recovery.serverConfirmedAt } : {}) };
  const replacements: OfflineRequest[] = [
    {
      ...upload, id: replacementIds[0], groupId: replacementGroupId, dedupeKey: keys[0], sequence: 0,
      body: JSON.stringify({ ...uploadBody, ...photo, idempotencyKey: keys[0] }), label: `${upload.label}（替换）`,
      status: "pending", attempts: 0, lastError: "", createdAt: now, updatedAt: now, backgroundEligible: false, recovery
    },
    {
      ...completion, id: replacementIds[1], groupId: replacementGroupId, dedupeKey: keys[1], sequence: 1,
      body: JSON.stringify({ ...completionBody, idempotencyKey: keys[1] }), label: `${completion.label}（替换后）`,
      status: "pending", attempts: 0, lastError: "", createdAt: new Date(new Date(now).getTime() + 1).toISOString(), updatedAt: now, backgroundEligible: false, recovery
    }
  ];
  const archived = group.map<ArchivedOfflineRequest>(item => ({
    ...item,
    archive: { reason: "photo_replaced", archivedAt: now, replacementGroupId, replacementRequestIds: replacementIds, replacementDedupeKeys: keys }
  }));
  return { archived, replacements };
}
